// Shared online/recent/offline/never-connected classifier.
//
// Previously duplicated (with the same 5-minute/60-minute thresholds) in
// DevicesOverview.jsx's getConnection() and Dashboard.js's getDeviceStatus() -
// factored out here so both of those plus the Experiment page's
// RunContextBar read the exact same thresholds from one place instead of a
// third copy silently drifting.
export const STATUS_ONLINE = "online";
export const STATUS_RECENT = "recent";
export const STATUS_OFFLINE = "offline";
export const STATUS_NEVER = "never";

/**
 * Classify connectivity from a device/controller's most recent timestamp
 * (e.g. last_seen, last_status_at - anything Date-parseable). Returns a
 * label plus both a Tailwind dot-background class and a text-color class,
 * since call sites render either or both.
 */
export function classifyDeviceStatus(lastSeenValue) {
  if (!lastSeenValue) {
    return {
      status: STATUS_NEVER,
      label: "Never connected",
      dotClassName: "bg-slate-300",
      textClassName: "text-slate-500",
    };
  }
  const minutes = (Date.now() - new Date(lastSeenValue).getTime()) / 60000;
  if (minutes < 5) {
    return {
      status: STATUS_ONLINE,
      label: "Online",
      dotClassName: "bg-emerald-500",
      textClassName: "text-emerald-700",
    };
  }
  if (minutes < 60) {
    return {
      status: STATUS_RECENT,
      label: "Recent",
      dotClassName: "bg-amber-500",
      textClassName: "text-amber-700",
    };
  }
  return {
    status: STATUS_OFFLINE,
    label: "Offline",
    dotClassName: "bg-rose-500",
    textClassName: "text-rose-700",
  };
}

// Numeric rank for sorting fleet lists best-connectivity-first.
export function deviceStatusSortOrder(lastSeenValue) {
  const order = { [STATUS_ONLINE]: 0, [STATUS_RECENT]: 1, [STATUS_OFFLINE]: 2, [STATUS_NEVER]: 3 };
  return order[classifyDeviceStatus(lastSeenValue).status];
}

/**
 * Greenhouse controllers report their own is_online boolean (derived
 * server-side from MQTT LWT, not just a stale timestamp) alongside
 * last_status_at - trust is_online for the online/offline split, and only
 * fall back to the timestamp-based classifier for the "never connected"
 * case and for a human-readable last-update time.
 */
export function classifyControllerStatus(controller) {
  if (!controller) return null;
  if (controller.is_online) {
    return {
      status: STATUS_ONLINE,
      label: "Online",
      dotClassName: "bg-emerald-500",
      textClassName: "text-emerald-700",
    };
  }
  const base = classifyDeviceStatus(controller.last_status_at);
  // is_online=false is authoritative - never report "online"/"recent" from
  // a stale timestamp when the controller has told us it's offline.
  if (base.status === STATUS_ONLINE || base.status === STATUS_RECENT) {
    return {
      status: STATUS_OFFLINE,
      label: "Offline",
      dotClassName: "bg-rose-500",
      textClassName: "text-rose-700",
    };
  }
  return base;
}
